import React from 'react';
import { useNavigate } from 'react-router-dom';
import { Zap } from 'lucide-react';
import { cn } from '@/lib/utils';
import { useBillingStore } from '@/stores/useBillingStore';
import { Badge } from '@/components/ui/badge';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';

interface SidebarUsageMeterProps {
  isExpanded: boolean;
  onNavigate?: () => void;
}

export const SidebarUsageMeter: React.FC<SidebarUsageMeterProps> = ({
  isExpanded,
  onNavigate,
}) => {
  const navigate = useNavigate();
  const billingStatus = useBillingStore((state) => state.status);

  const handleClick = () => {
    onNavigate?.();
    navigate('/billing');
  };

  // If status is not yet loaded, render a subtle skeleton
  if (!billingStatus) {
    if (!isExpanded) {
      return (
        <div className="flex justify-center py-1">
          <div className="h-8 w-8 rounded-lg bg-muted/40 animate-pulse" />
        </div>
      );
    }
    return (
      <div className="rounded-lg border border-border/40 bg-muted/20 p-2.5 animate-pulse">
        <div className="flex items-center justify-between mb-2">
          <div className="h-3 w-16 bg-muted rounded" />
          <div className="h-3 w-10 bg-muted rounded" />
        </div>
        <div className="h-1.5 w-full bg-muted rounded" />
      </div>
    );
  }

  const usedConversations = billingStatus.conversationsUsed ?? 0;
  const totalLimit = billingStatus.maxConversations || (billingStatus.isSubscribed ? 5000 : 5);
  const percentage = totalLimit > 0 ? Math.min(100, Math.round((usedConversations / totalLimit) * 100)) : 0;
  const isExceeded = usedConversations >= totalLimit;
  const isNearLimit = !isExceeded && percentage >= 80;
  const planName =
    billingStatus.planName ||
    (billingStatus.planId === 'starter'
      ? 'Starter'
      : billingStatus.planId === 'enterprise'
      ? 'Enterprise'
      : billingStatus.isSubscribed
      ? 'Pro'
      : 'Gratuito');

  // Collapsed view (icon with dot indicator and rich tooltip)
  if (!isExpanded) {
    return (
      <Tooltip>
        <TooltipTrigger asChild>
          <button
            onClick={handleClick}
            className={cn(
              'relative h-9 w-9 rounded-lg flex items-center justify-center transition-colors mx-auto focus:outline-none focus-visible:ring-2 focus-visible:ring-ring',
              isExceeded
                ? 'bg-destructive/10 text-destructive hover:bg-destructive/20'
                : isNearLimit
                ? 'bg-amber-500/10 text-amber-500 hover:bg-amber-500/20'
                : 'text-muted-foreground hover:bg-muted hover:text-foreground'
            )}
            aria-label="Cota de Conversas IA"
          >
            <Zap
              className={cn(
                'h-4 w-4 transition-colors',
                isExceeded
                  ? 'text-destructive fill-destructive'
                  : isNearLimit
                  ? 'text-amber-500 fill-amber-500'
                  : ''
              )}
            />
            {(isNearLimit || isExceeded) && (
              <span
                className={cn(
                  'absolute top-1.5 right-1.5 h-2 w-2 rounded-full ring-2 ring-card',
                  isExceeded ? 'bg-destructive animate-pulse' : 'bg-amber-500'
                )}
              />
            )}
          </button>
        </TooltipTrigger>
        <TooltipContent side="right" className="space-y-1.5 p-2.5 max-w-xs text-xs">
          <div className="flex items-center justify-between gap-3 font-semibold">
            <span>Conversas ({planName})</span>
            <span
              className={
                isExceeded
                  ? 'text-destructive font-bold'
                  : isNearLimit
                  ? 'text-amber-500 font-bold'
                  : 'text-foreground'
              }
            >
              {isExceeded ? '100% (Pausado)' : `${percentage}%`}
            </span>
          </div>
          <p className="text-muted-foreground text-[11px]">
            {usedConversations.toLocaleString()} de {totalLimit.toLocaleString()} conversas usadas
          </p>
          {isExceeded && (
            <p className="text-destructive font-medium text-[11px]">
              Limite atingido — Atendimento pausado
            </p>
          )}
          {isNearLimit && (
            <p className="text-amber-500 font-medium text-[11px]">
              Limite se aproximando ({percentage}%)
            </p>
          )}
          <p className="text-[10px] text-muted-foreground/80 pt-1 border-t border-border/40">
            Clique para gerenciar plano
          </p>
        </TooltipContent>
      </Tooltip>
    );
  }

  // Expanded view (card with progress bar and plan badge)
  return (
    <div
      onClick={handleClick}
      role="button"
      tabIndex={0}
      onKeyDown={(e) => {
        if (e.key === 'Enter' || e.key === ' ') {
          e.preventDefault();
          handleClick();
        }
      }}
      className={cn(
        'group block w-full rounded-lg border p-2.5 transition-all text-left cursor-pointer focus:outline-none focus-visible:ring-2 focus-visible:ring-ring select-none',
        isExceeded
          ? 'border-destructive/30 bg-destructive/5 hover:bg-destructive/10'
          : isNearLimit
          ? 'border-amber-500/30 bg-amber-500/5 hover:bg-amber-500/10'
          : 'border-border/60 bg-muted/30 hover:bg-muted/60'
      )}
    >
      {/* Header: Title, Icon, Badge */}
      <div className="flex items-center justify-between gap-1.5 mb-1.5">
        <div className="flex items-center gap-1.5 min-w-0">
          <Zap
            className={cn(
              'h-3.5 w-3.5 shrink-0 transition-colors',
              isExceeded
                ? 'text-destructive fill-destructive'
                : isNearLimit
                ? 'text-amber-500 fill-amber-500'
                : 'text-primary fill-primary'
            )}
          />
          <span className="text-xs font-semibold text-foreground truncate">
            Conversas
          </span>
        </div>
        <Badge
          variant={isExceeded ? 'destructive' : 'secondary'}
          className={cn(
            'text-[10px] px-1.5 py-0 h-4 font-bold shrink-0',
            !isExceeded && 'bg-primary/10 text-primary border-primary/20'
          )}
        >
          {planName}
        </Badge>
      </div>

      {/* Progress Bar */}
      <div className="h-1.5 w-full bg-secondary rounded-full overflow-hidden mb-1.5 border border-border/30">
        <div
          className={cn(
            'h-full rounded-full transition-all duration-500 ease-out',
            isExceeded
              ? 'bg-destructive'
              : isNearLimit
              ? 'bg-amber-500'
              : 'bg-primary'
          )}
          style={{ width: `${percentage}%` }}
        />
      </div>

      {/* Numbers & Percentage / Alert */}
      <div className="flex items-center justify-between text-[11px]">
        <span className="text-muted-foreground font-medium truncate">
          {usedConversations.toLocaleString()} / {totalLimit.toLocaleString()}
        </span>
        <span
          className={cn(
            'font-bold ml-1 shrink-0',
            isExceeded
              ? 'text-destructive'
              : isNearLimit
              ? 'text-amber-600 dark:text-amber-400'
              : 'text-muted-foreground'
          )}
        >
          {isExceeded ? 'Pausado' : `${percentage}%`}
        </span>
      </div>
    </div>
  );
};
